/**
 * モーショングラフィックス生成の品質モードの選択「ライト / スタンダード / ダメ出し」(docs/features/brand-cut-motion-graphics-plan.md §10.10.1・
 * 2026-10-01 利用者決定で 3 段・2026-10-02 に呼び名と経路ごとの選択肢 / 既定を決定)。ブランドカット (作り方 = モーション・スタンダード / ダメ出しのみ)・
 * シーンのモーション生成・全編のモーション生成・レイヤー素材 (プロンプト入力モード) に置く。選択肢と既定は route (MOTION_QUALITY_ROUTES) で決まり、
 * 状態は features/motion/quality の経路別ストア (localStorage 'motion_generation_quality.<route>') に覚える。
 * 暗い面 (シナリオ画面・モーダル) 向けの文字色。選べない値 (unavailable) が保存されていれば経路の既定 (それも選べなければ先頭) を選択中として表示する。
 */
import {
  MOTION_QUALITY_DESCRIPTIONS,
  MOTION_QUALITY_HEADING,
  MOTION_QUALITY_LABELS,
  MOTION_QUALITY_ROUTES,
  motionQualityFor,
  type MotionQuality,
  type MotionQualityRoute,
  useMotionQuality,
} from '../../../features/motion/quality';

export function MotionQualitySelect({
  route,
  disabled,
  compact = false,
  testId = 'motion-quality',
  unavailable = [],
  notes,
}: {
  /** 生成の経路 (選択肢・既定・保存先が決まる) */
  route: MotionQualityRoute;
  disabled?: boolean;
  /** true: 説明をツールチップ (title) にして 1 行で出す (シーンの動画欄など狭い場所) */
  compact?: boolean;
  testId?: string;
  /** 出すが選べない選択肢 (全編でライトの料金が未設定のとき等) */
  unavailable?: readonly MotionQuality[];
  /** 選択肢ごとの補足 (料金など)。説明の後ろに足す */
  notes?: Partial<Record<MotionQuality, string>>;
}) {
  const { options, defaultQuality } = MOTION_QUALITY_ROUTES[route];
  const [stored, setQuality] = useMotionQuality(route);
  const selectable = options.filter((q) => !unavailable.includes(q));
  const quality = motionQualityFor(stored, selectable, defaultQuality);
  const describe = (q: MotionQuality) => [MOTION_QUALITY_DESCRIPTIONS[q], notes?.[q]].filter(Boolean).join('・');
  return (
    <div
      style={{
        display: 'inline-flex', flexDirection: compact ? 'row' : 'column', alignItems: compact ? 'center' : 'flex-start', gap: compact ? 8 : 4,
        fontSize: 13, color: 'rgba(245, 241, 230, 0.9)', opacity: disabled ? 0.6 : 1, textAlign: 'left',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {!compact && <span style={{ whiteSpace: 'nowrap' }}>{MOTION_QUALITY_HEADING}</span>}
        <div
          role="radiogroup"
          aria-label={MOTION_QUALITY_HEADING}
          data-testid={testId}
          style={{ display: 'inline-flex', border: '1px solid rgba(245, 241, 230, 0.25)', borderRadius: 6, overflow: 'hidden' }}
        >
          {options.map((q, i) => {
            const checked = q === quality;
            const off = disabled || unavailable.includes(q);
            return (
              <button
                key={q}
                type="button"
                role="radio"
                aria-checked={checked}
                disabled={off}
                title={describe(q)}
                data-testid={`${testId}-${q}`}
                onClick={() => setQuality(q)}
                style={{
                  padding: compact ? '2px 8px' : '3px 10px', fontSize: 12, whiteSpace: 'nowrap',
                  border: 'none', borderLeft: i > 0 ? '1px solid rgba(245, 241, 230, 0.25)' : 'none',
                  background: checked ? 'rgba(245, 241, 230, 0.18)' : 'transparent',
                  color: off && !checked ? 'rgba(245, 241, 230, 0.35)' : 'rgba(245, 241, 230, 0.9)',
                  fontWeight: checked ? 600 : 400, cursor: off ? 'default' : 'pointer',
                }}
              >
                {MOTION_QUALITY_LABELS[q]}
              </button>
            );
          })}
        </div>
      </div>
      {!compact && (
        <span style={{ fontSize: 12, color: 'rgba(245, 241, 230, 0.55)' }} data-testid={`${testId}-description`}>{describe(quality)}</span>
      )}
    </div>
  );
}
